import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { StoryService } from '../services/APIService';
import { baseURL } from '../services/APIService';
// Async thunk to fetch all success stories
export const fetchSuccessStories = createAsyncThunk(
  'successStories/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const response = await StoryService.getAllStories();
      return response;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch success stories');
    }
  }
);

// Async thunk to fetch a specific success story by ID
export const fetchStoryById = createAsyncThunk(
  'successStories/fetchById',
  async (storyId, { rejectWithValue }) => {
    try {
      const response = await StoryService.getStory(storyId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message || `Failed to fetch story with ID: ${storyId}`);
    }
  }
);

// Async thunk to add a new success story (admin only)
export const createStory = createAsyncThunk(
  'successStories/add',
  async (storyData, { rejectWithValue }) => {
    try {
    
      const response = await StoryService.createStory(storyData);
      return response.story || response; // Handle both formats: { story: {...} } or direct object
    } catch (error) {
      console.error('Redux thunk: Story creation error:', error);
      // Handle different error formats
      if (typeof error === 'object' && error.error) {
        return rejectWithValue(error.error.message || error.message || 'Failed to add new success story');
      }
      return rejectWithValue(error.message || 'Failed to add new success story');
    }
  }
);

// Async thunk to update a success story (admin only)
export const updateStory = createAsyncThunk(
  'successStories/update',
  async ({ id, storyData }, { rejectWithValue }) => {
    try {
  
      const response = await StoryService.updateStory(id, storyData);
      return response.story || response; // Handle both formats: { story: {...} } or direct object
    } catch (error) {
      console.error('Redux thunk: Story update error:', error);
      // Handle different error formats
      if (typeof error === 'object' && error.error) {
        return rejectWithValue(error.error.message || error.message || `Failed to update success story with ID: ${id}`);
      }
      return rejectWithValue(error.message || `Failed to update success story with ID: ${id}`);
    }
  }
);

// Async thunk to delete a success story (admin only)
export const deleteStory = createAsyncThunk(
  'successStories/delete',
  async (storyId, { rejectWithValue }) => {
    try {
      await StoryService.deleteStory(storyId);
      return storyId;
    } catch (error) {
      return rejectWithValue(error.message || `Failed to delete success story with ID: ${storyId}`);
    }
  }
);

const initialState = {
  title: "Success Stories",
  stories: [],
  currentStory: null,
  status: 'idle', // 'idle', 'loading', 'succeeded', 'failed'
  error: null
};

const successStoriesSlice = createSlice({
  name: 'successStories',
  initialState,
  reducers: {
    clearCurrentStory(state) {
      state.currentStory = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch all stories
      .addCase(fetchSuccessStories.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchSuccessStories.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.title = 'Success Stories';
        // Handle the API response format correctly
        state.stories = action.payload.data || [];
        
        // Add image_url to each story based on hasImage flag
        state.stories = state.stories.map(story => {
          // Define baseURL for image paths
          
          return {
            ...story,
            name: story.title,
            description: story.content,
            person_name: story.person_name || '',
            image: story.hasImage ? `${baseURL}/stories/image/${story.id}` : null,
            created_at: story.created_at
          };
        });
      })
      .addCase(fetchSuccessStories.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      
      // Fetch story by ID
      .addCase(fetchStoryById.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchStoryById.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.currentStory = action.payload;
      })
      .addCase(fetchStoryById.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      
      // Create story
      .addCase(createStory.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(createStory.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.stories.push(action.payload);
      })
      .addCase(createStory.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      
      // Update story
      .addCase(updateStory.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(updateStory.fulfilled, (state, action) => {
        state.status = 'succeeded';
        const index = state.stories.findIndex(story => story.id === action.payload.id);
        if (index !== -1) {
          state.stories[index] = action.payload;
        }
        if (state.currentStory && state.currentStory.id === action.payload.id) {
          state.currentStory = action.payload;
        }
      })
      .addCase(updateStory.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      
      // Delete story
      .addCase(deleteStory.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(deleteStory.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.stories = state.stories.filter(story => story.id !== action.payload);
        if (state.currentStory && state.currentStory.id === action.payload) {
          state.currentStory = null;
        }
      })
      .addCase(deleteStory.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      });
  }
});

// Export actions and selectors
export const { clearCurrentStory } = successStoriesSlice.actions;

export const selectAllStories = state => state.successStories.stories;
export const selectCurrentStory = state => state.successStories.currentStory;
export const selectStoriesStatus = state => state.successStories.status;
export const selectStoriesError = state => state.successStories.error;
export const selectStoriesTitle = state => state.successStories.title;

export default successStoriesSlice.reducer;
